import React from 'react';
import { Loader2, Trash2 } from 'lucide-react';

type ModalStatus = 'loading' | 'success' | 'closed';

interface ClassDeletionStatusModalProps {
    status: ModalStatus;
    isRecurring: boolean;
    deleteCount?: number;
    onClose: () => void;
}

/**
 * A styled modal for showing class deletion progress and confirmation.
 * Displays a loading spinner while deleting, then a success indicator.
 * For recurring deletions, shows the count of deleted classes.
 */
const ClassDeletionStatusModal: React.FC<ClassDeletionStatusModalProps> = ({
    status,
    isRecurring,
    deleteCount = 1,
    onClose
}) => {
    if (status === 'closed') return null;

    const isLoading = status === 'loading';
    
    // Title based on state and type
    const title = isLoading
        ? (isRecurring ? 'Deleting Classes...' : 'Deleting Class...')
        : (isRecurring ? `${deleteCount} Classes Deleted` : 'Class Deleted');

    return (
        <div 
            className="fixed inset-0 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
            style={{ zIndex: 60 }}
        >
            <div className="bg-slate-900 shadow-2xl w-full max-w-sm overflow-hidden border border-slate-700 rounded-xl">
                <div className="p-8 text-center space-y-6">
                    {/* Icon */}
                    <div className="flex justify-center">
                        {isLoading ? (
                            <div className="w-16 h-16 rounded-full bg-rose-900/30 flex items-center justify-center border border-rose-700/50">
                                <Loader2 size={32} className="text-rose-400 animate-spin" />
                            </div>
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-rose-900/30 flex items-center justify-center border border-rose-700/50 animate-in zoom-in duration-300">
                                <Trash2 size={32} className="text-rose-400" />
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <h3 className="font-serif text-xl text-slate-100">{title}</h3>

                    {/* Description */}
                    <p className="text-slate-400 text-sm">
                        {isLoading 
                            ? (isRecurring 
                                ? 'Removing recurring classes from the schedule...' 
                                : 'Removing class from the schedule...')
                            : (isRecurring 
                                ? `Successfully removed ${deleteCount} upcoming class${deleteCount !== 1 ? 'es' : ''}.`
                                : 'The class has been removed from the schedule.')
                        }
                    </p>

                    {/* Close Button (only show when not loading) */}
                    {!isLoading && (
                        <button
                            onClick={onClose}
                            className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors"
                        >
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClassDeletionStatusModal;
